import { zonedDayKey } from './kernelTimezone';
import { parseDbDate } from './kernelFormat';
import { searchTerms, matchesAllTerms } from './kernelSearch';
// Logica pura delle viste di un progetto: filtri, ordinamento, ricerca,
// raggruppamento e colonne visibili/ordinate. Nessuna dipendenza da React.

export const TICKETS_COLUMN = 'tickets'; // pseudo-colonna "Ticket collegati" (filtrabile)

// Tipi di colonna e relativa icona (nomi di NtIcon).
export const TYPE_ICONS = {
  title: 'text',
  text: 'text',
  number: 'hash',
  select: 'tag',
  status: 'status',
  multi_select: 'list',
  date: 'calendar',
  daterange: 'calendar',
  person: 'users',
  checkbox: 'priority',
  url: 'link',
  email: 'mail',
  phone: 'phone',
  files: 'paperclip',
  task_relation: 'link',
  created_time: 'clock',
  updated_time: 'clock',
  created_by: 'user',
  updated_by: 'user',
};

const TEXT_TYPES = ['title', 'text', 'url', 'email', 'phone'];
const DATE_TYPES = ['date', 'created_time', 'updated_time'];

const OPS = {
  text: ['contains', 'not_contains', 'is', 'is_not', 'empty', 'not_empty'],
  number: ['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'empty', 'not_empty'],
  select: ['is', 'is_not', 'empty', 'not_empty'],
  multi_select: ['contains', 'not_contains', 'empty', 'not_empty'],
  date: ['is', 'before', 'after', 'on_or_before', 'on_or_after', 'empty', 'not_empty'],
  daterange: ['is', 'before', 'after', 'empty', 'not_empty'],
  person: ['is', 'is_not', 'empty', 'not_empty'],
  checkbox: ['checked', 'unchecked'],
  files: ['empty', 'not_empty'],
  task_relation: ['empty', 'not_empty'],
  tickets: ['empty', 'not_empty'],
};

export const NO_VALUE_OPS = ['empty', 'not_empty', 'checked', 'unchecked'];

/** Famiglia di operatori di una colonna (o della pseudo-colonna dei ticket). */
export const opFamily = (column) => {
  if (!column) return 'text';
  const type = column.type;
  if (TEXT_TYPES.includes(type)) return 'text';
  if (DATE_TYPES.includes(type)) return 'date';
  if (type === 'select' || type === 'status') return 'select';
  if (type === 'created_by' || type === 'updated_by') return 'person';
  return type in OPS ? type : 'text';
};

export const opsForColumn = (column) => OPS[opFamily(column)];

/** Colonna "virtuale" per i ticket collegati, da trattare come le altre nei filtri. */
export const ticketsPseudoColumn = (label) => ({ id: TICKETS_COLUMN, type: 'tickets', name: label, options: [] });

/** Valore di una cella in una forma confrontabile. */
export const valueOf = (task, column) => {
  if (!column) return undefined;
  switch (column.type) {
    case 'title': return task.title;
    case 'tickets': return task.tickets ? task.tickets.length : 0;
    case 'created_time': return task.created_at ? zonedDayKey(parseDbDate(task.created_at)) : undefined;
    case 'updated_time': return task.updated_at ? zonedDayKey(parseDbDate(task.updated_at)) : undefined;
    case 'created_by': return task.created_by_user_id ?? undefined;
    case 'updated_by': return task.updated_by_user_id ?? undefined;
    default: return task.data ? task.data[String(column.id)] : undefined;
  }
};

/** Le persone di una cella: un elenco di id (o un solo id, come nei valori salvati in precedenza e nelle colonne "creato da"). */
export const personIds = (v) => (Array.isArray(v) ? v : (v === null || v === undefined || v === '' ? [] : [v])).map(Number).filter(Boolean);

const isEmptyValue = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

export const todayIso = () => {
  return zonedDayKey(new Date());
};

const resolveDate = (value) => (value === 'today' ? todayIso() : value);

/** Una regola di filtro applicata a un'attività. `ctx.userId` serve per il valore "io". */
export const matchRule = (task, rule, columnsById, ctx) => {
  const column = columnsById[String(rule.column)];
  if (!column) return true; // colonna eliminata: la regola non filtra
  const value = valueOf(task, column);
  const family = opFamily(column);
  const target = rule.value;

  switch (rule.op) {
    case 'empty': return family === 'checkbox' ? !value : (family === 'tickets' ? value === 0 : isEmptyValue(value) || (family === 'daterange' && !value.start && !value.end));
    case 'not_empty': return family === 'checkbox' ? Boolean(value) : (family === 'tickets' ? value > 0 : !(isEmptyValue(value) || (family === 'daterange' && !value.start && !value.end)));
    case 'checked': return Boolean(value);
    case 'unchecked': return !value;
    default: break;
  }

  if (isEmptyValue(target)) return true; // regola incompleta: non filtra

  if (family === 'text') {
    const text = String(value ?? '').toLowerCase();
    const needle = String(target).toLowerCase();
    if (rule.op === 'contains') return text.includes(needle);
    if (rule.op === 'not_contains') return !text.includes(needle);
    if (rule.op === 'is') return text === needle;
    if (rule.op === 'is_not') return text !== needle;
  }
  if (family === 'number') {
    if (isEmptyValue(value)) return false;
    const n = Number(value);
    const t = Number(target);
    return { eq: n === t, neq: n !== t, gt: n > t, lt: n < t, gte: n >= t, lte: n <= t }[rule.op] ?? true;
  }
  if (family === 'select') {
    if (rule.op === 'is') return value === target;
    if (rule.op === 'is_not') return value !== target;
  }
  if (family === 'multi_select') {
    const list = Array.isArray(value) ? value : [];
    if (rule.op === 'contains') return list.includes(target);
    if (rule.op === 'not_contains') return !list.includes(target);
  }
  if (family === 'person') {
    const wanted = target === 'me' ? ctx.userId : Number(target);
    const ids = personIds(value);
    if (rule.op === 'is') return ids.includes(wanted);
    if (rule.op === 'is_not') return !ids.includes(wanted);
  }
  if (family === 'date' || family === 'daterange') {
    const day = resolveDate(target);
    const start = family === 'daterange' ? value?.start : value;
    const end = family === 'daterange' ? (value?.end || value?.start) : value;
    if (!start && !end) return false;
    const from = start || end;
    const to = end || start;
    switch (rule.op) {
      case 'is': return from <= day && day <= to;
      case 'before': return to < day;
      case 'after': return from > day;
      case 'on_or_before': return from <= day;
      case 'on_or_after': return to >= day;
      default: return true;
    }
  }
  return true;
};

export const applyFilter = (tasks, filter, columnsById, ctx) => {
  const rules = filter?.rules || [];
  if (rules.length === 0) return tasks;
  const test = (task) => (filter.mode === 'or'
    ? rules.some((rule) => matchRule(task, rule, columnsById, ctx))
    : rules.every((rule) => matchRule(task, rule, columnsById, ctx)));
  return tasks.filter(test);
};

/** Quante regole sono davvero attive (colonna esistente). */
export const activeRuleCount = (filter, columnsById) => (filter?.rules || []).filter((r) => columnsById[String(r.column)]).length;

const personName = (users, id) => {
  const u = users.find((x) => x.id === id);
  return u ? [u.name, u.surname].filter(Boolean).join(' ') : '';
};

/** Testo cercabile di una cella (le date non si cercano) (etichette delle opzioni e nomi delle persone inclusi). */
const searchText = (task, column, users) => {
  const value = valueOf(task, column);
  if (isEmptyValue(value)) return '';
  switch (column.type) {
    case 'select':
    case 'status': return column.options.find((o) => o.value === value)?.label || '';
    case 'multi_select': return (Array.isArray(value) ? value : []).map((v) => column.options.find((o) => o.value === v)?.label || '').join(' ');
    case 'person':
    case 'created_by':
    case 'updated_by': return personIds(value).map((id) => personName(users, id)).join(' ');
    case 'files': return (Array.isArray(value) ? value : []).map((f) => f.name || '').join(' ');
    case 'checkbox':
    case 'date':
    case 'daterange':
    case 'created_time':
    case 'updated_time': return '';
    default: return String(value);
  }
};

/** Più parole separate da spazio: devono comparire tutte (AND), anche in colonne diverse. Maiuscole e accenti non contano. */
export const applySearch = (tasks, query, columns, users) => {
  const terms = searchTerms(query);
  if (terms.length === 0) return tasks;
  return tasks.filter((task) => matchesAllTerms(columns.map((column) => searchText(task, column, users)).join(' '), terms));
};

const compareValues = (a, b, column, users) => {
  switch (column.type) {
    case 'number': return Number(a) - Number(b);
    case 'checkbox': return Number(Boolean(a)) - Number(Boolean(b));
    case 'select':
    case 'status': {
      const order = (v) => column.options.findIndex((o) => o.value === v);
      return order(a) - order(b);
    }
    case 'multi_select': return String(searchText({ data: { [column.id]: a } }, column, users)).localeCompare(searchText({ data: { [column.id]: b } }, column, users));
    case 'person':
    case 'created_by':
    case 'updated_by': return personIds(a).map((id) => personName(users, id)).join(' ').localeCompare(personIds(b).map((id) => personName(users, id)).join(' '));
    case 'daterange': return String(a?.start || a?.end || '').localeCompare(String(b?.start || b?.end || ''));
    case 'files': return (a?.length || 0) - (b?.length || 0);
    default: return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }
};

/** Ordina per più criteri; le celle vuote vanno sempre in fondo. */
export const applySorts = (tasks, sorts, columnsById, users) => {
  const active = (sorts || []).filter((s) => columnsById[String(s.column)]);
  if (active.length === 0) return tasks;
  return [...tasks].sort((x, y) => {
    for (const sort of active) {
      const column = columnsById[String(sort.column)];
      const a = valueOf(x, column);
      const b = valueOf(y, column);
      const ea = isEmptyValue(a);
      const eb = isEmptyValue(b);
      if (ea && eb) continue;
      if (ea) return 1;
      if (eb) return -1;
      const diff = compareValues(a, b, column, users);
      if (diff !== 0) return sort.dir === 'desc' ? -diff : diff;
    }
    return 0;
  });
};

/** Colonne per cui il raggruppamento ha senso. */
export const GROUPABLE_TYPES = ['select', 'status', 'person', 'checkbox'];

/** Raggruppa le attività: una sezione per ogni valore (più "senza valore"). */
export const groupTasks = (tasks, column, users) => {
  const groups = [];
  const find = (key) => groups.find((g) => g.key === key);
  const push = (key, label, option) => {
    groups.push({ key, label, option, tasks: [] });
  };
  if (column.type === 'select' || column.type === 'status') {
    column.options.forEach((o) => push(o.value, o.label, o));
  } else if (column.type === 'checkbox') {
    push('true', column.name, null);
    push('false', null, null);
  } else {
    users.forEach((u) => push(String(u.id), personName(users, u.id), null));
  }
  push('', null, null);
  tasks.forEach((task) => {
    const v = valueOf(task, column);
    let key = '';
    if (column.type === 'checkbox') key = v ? 'true' : 'false';
    else if (column.type === 'person') key = String(personIds(v)[0] ?? ''); // una persona sola: la prima
    else if (!isEmptyValue(v)) key = String(v);
    (find(key) || find('')).tasks.push(task);
  });
  // Le sezioni vuote restano visibili solo per select/stato (si può trascinare/aggiungere lì), tranne "senza valore".
  return groups.filter((g) => g.tasks.length > 0 || ((column.type === 'select' || column.type === 'status') && g.key !== ''));
};

/** Valore da scrivere in una nuova attività creata dentro una sezione. */
export const groupKeyToValue = (column, key) => {
  if (key === '') return null;
  if (column.type === 'checkbox') return key === 'true' ? true : null;
  if (column.type === 'person') return [Number(key)];
  return key;
};

/** Colonne visibili nell'ordine della vista: il titolo è sempre il primo e sempre visibile. */
export const orderedColumns = (columns, config) => {
  const hidden = config?.hidden || [];
  const order = config?.order || [];
  const rank = (c) => {
    const i = order.indexOf(c.id);
    return i === -1 ? order.length + columns.indexOf(c) : i;
  };
  const title = columns.filter((c) => c.type === 'title');
  const others = columns.filter((c) => c.type !== 'title').sort((a, b) => rank(a) - rank(b));
  return [...title, ...others.filter((c) => !hidden.includes(c.id))];
};

/** Tutte le colonne nell'ordine della vista (anche nascoste): per il pannello delle proprietà. */
export const allColumnsInOrder = (columns, config) => {
  const order = config?.order || [];
  const rank = (c) => {
    const i = order.indexOf(c.id);
    return i === -1 ? order.length + columns.indexOf(c) : i;
  };
  return [...columns.filter((c) => c.type === 'title'), ...columns.filter((c) => c.type !== 'title').sort((a, b) => rank(a) - rank(b))];
};

export const DEFAULT_WIDTH = 170;
export const TITLE_WIDTH = 280;
export const MIN_WIDTH = 80;

export const columnWidth = (config, column) => (config?.widths && config.widths[column.id]) || (column.type === 'title' ? TITLE_WIDTH : DEFAULT_WIDTH);

/**
 * Righe in ordine di albero: ogni attività seguita dalle sue sotto-attività
 * (a meno che sia compressa). Le attività con padre assente dall'elenco
 * (filtrato) risultano di primo livello.
 */
export const flattenTree = (tasks, collapsed = {}) => {
  const ids = new Set(tasks.map((task) => task.id));
  const children = {};
  tasks.forEach((task) => {
    const parent = task.parent_id && ids.has(task.parent_id) ? task.parent_id : 0;
    (children[parent] = children[parent] || []).push(task);
  });
  const rows = [];
  const walk = (parent, depth) => (children[parent] || []).forEach((task) => {
    const kids = children[task.id] || [];
    rows.push({ task, depth, childCount: kids.length, collapsed: Boolean(collapsed[task.id]) });
    if (!collapsed[task.id]) walk(task.id, depth + 1);
  });
  walk(0, 0);
  return rows;
};

/** Id dell'attività e di tutte le sue discendenti. */
export const descendantIds = (tasks, id) => {
  const children = {};
  tasks.forEach((task) => {
    if (task.parent_id) (children[task.parent_id] = children[task.parent_id] || []).push(task.id);
  });
  const result = new Set([id]);
  const queue = [id];
  while (queue.length > 0) {
    (children[queue.shift()] || []).forEach((childId) => {
      if (!result.has(childId)) {
        result.add(childId);
        queue.push(childId);
      }
    });
  }
  return result;
};
